/**
 * @file Customers.js
 * @description Page component for managing customer directory, search/filtering by SSN or state, and viewing profile avatars.
 */

import '../css/pages/Customers.css';
import React, { useState, useEffect, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import Swal from "sweetalert2";
import { getCustomers, deleteCustomer, blockCustomer, unblockCustomer } from "../api/services";
import toast from "react-hot-toast";
import { formatDate, formatName, capitalizeName } from "../utils/formatDate";
import { imgUrl } from "../utils/imgUrl";

/* ── US States ─────────────────────────────────────────────── */
const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN",
  "IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV",
  "NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN",
  "TX","UT","VT","VA","WA","WV","WI","WY",
];

/**
 * SsnCell component for masked SSN rendering with toggle visibility button.
 * @component
 * @param {Object} props
 * @param {string} props.ssn - Social security number string.
 */
function SsnCell({ ssn }) {
  const [visible, setVisible] = useState(false);
  const masked = "***-**-" + String(ssn).replace(/\D/g, "").slice(-4);
  return (
    <div className="ssn-cell">
      <span className="ssn-value">{visible ? ssn : masked}</span>
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        title={visible ? "Hide SSN" : "Show SSN"}
        className={`ssn-toggle ${visible ? "ssn-toggle-active" : ""}`}
      >
        {visible ? "🙈" : "👁"}
      </button>
    </div>
  );
}

/**
 * PhotoPopup component for displaying enlarged customer profile photos.
 * @component
 * @param {Object} props
 * @param {string} props.src - Image URL.
 * @param {string} props.name - Customer full name.
 * @param {Function} props.onClose - Modal close handler.
 */
function PhotoPopup({ src, name, onClose }) {
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);
  return (
    <div className="photo-popup-overlay" onClick={onClose}>
      <div className="photo-popup" onClick={(e) => e.stopPropagation()}>
        <button className="photo-popup-close" onClick={onClose} aria-label="Close">✕</button>
        <img src={src} alt={name} className="photo-popup-img" />
        <div className="photo-popup-name">{name}</div>
      </div>
    </div>
  );
}

/**
 * AvatarCell component for rendering customer profile avatar or initials badge with popup viewer.
 * @component
 * @param {Object} props
 * @param {Object} props.customer - Customer details object.
 */
function AvatarCell({ customer }) {
  const [popupOpen, setPopupOpen] = useState(false);
  const initials = `${customer.firstName?.[0] || ""}${customer.lastName?.[0] || ""}`.toUpperCase();
  const photoSrc = imgUrl(customer.photo?.profile);
  const fullName = `${capitalizeName(customer.firstName)}${customer.middleName ? " " + customer.middleName[0].toUpperCase() + "." : ""} ${capitalizeName(customer.lastName)}`;

  return (
    <>
      <div className="customer-cell">
        <div
          className={`customer-avatar ${customer.isActive ? "customer-avatar-active" : "customer-avatar-inactive"} ${photoSrc ? "customer-avatar-clickable" : ""}`}
          onClick={(e) => { if (photoSrc) { e.stopPropagation(); setPopupOpen(true); } }}
          title={photoSrc ? "Click to view photo" : ""}
        >
          {photoSrc ? (
            <img src={photoSrc} alt={initials} className="customer-avatar-img" />
          ) : (
            <span className="customer-avatar-initials">{initials}</span>
          )}
        </div>
        <div>
          <div className="customer-name">
            {capitalizeName(customer.firstName)}
            {customer.middleName ? " " + customer.middleName[0].toUpperCase() + "." : ""}
          </div>
          <div className="customer-id">#{customer._id.slice(-6).toUpperCase()}</div>
        </div>
      </div>
      {popupOpen && photoSrc && (
        <PhotoPopup src={photoSrc} name={fullName} onClose={() => setPopupOpen(false)} />
      )}
    </>
  );
}

/* ── Confirm Deactivate Modal ──────────────────────────────── */
function ConfirmModal({ isOpen, onConfirm, onCancel, customerName }) {
  useEffect(() => {
    if (!isOpen) return;
    const h = (e) => { if (e.key === "Escape") onCancel(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [isOpen, onCancel]);
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-body" style={{ textAlign: "center" }}>
          <div className="confirm-icon">⚠️</div>
          <h2 style={{ marginBottom: 8 }}>Are you sure?</h2>
          <p className="text-muted" style={{ marginBottom: 8 }}>You are about to deactivate</p>
          <div className="confirm-name">{customerName}</div>
          <p className="text-muted text-sm" style={{ marginTop: 8, marginBottom: 24 }}>
            This customer will be marked as <strong>inactive</strong>.<br />
            You can reactivate them later if needed.
          </p>
          <div style={{ display: "flex", gap: 12 }}>
            <button onClick={onCancel} className="btn btn-outline w-full">Cancel</button>
            <button onClick={onConfirm} className="btn btn-danger w-full">Yes, Deactivate</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── DataTable custom styles ────────────────────────────────── */
const dtStyles = {
  headRow: {
    style: {
      backgroundColor: "var(--gray-4)",
      borderBottom: "1px solid var(--gray-3)",
      minHeight: "42px",
    },
  },
  headCells: {
    style: {
      fontSize: "11.5px",
      fontWeight: "700",
      color: "var(--gray-1)",
      textTransform: "uppercase",
      letterSpacing: "0.6px",
      paddingLeft: "16px",
      paddingRight: "16px",
    },
  },
  rows: {
    style: {
      fontSize: "13.5px",
      color: "var(--dark)",
      borderBottom: "1px solid var(--gray-4)",
      minHeight: "64px",
      cursor: "pointer",
      "&:hover": { backgroundColor: "rgba(26,122,74,0.04)" },
    },
  },
  cells: {
    style: { paddingLeft: "16px", paddingRight: "16px" },
  },
  pagination: {
    style: {
      borderTop: "1px solid var(--gray-3)",
      fontSize: "13px",
      color: "var(--gray-1)",
      minHeight: "52px",
    },
    pageButtonsStyle: {
      borderRadius: "6px",
      color: "var(--dark)",
      fill: "var(--gray-1)",
      "&:hover:not(:disabled)": { backgroundColor: "var(--gray-4)" },
      "&:focus": { outline: "none", backgroundColor: "var(--gray-4)" },
    },
  },
  noData: {
    style: { padding: "60px 20px", color: "var(--gray-1)", fontSize: "13.5px" },
  },
  progress: {
    style: { padding: "40px" },
  },
};

/* ── Conditional row styles ─────────────────────────────────── */
const conditionalRowStyles = [
  { when: (r) => r.isBlocked,  style: { backgroundColor: "rgba(224,60,60,0.04)" } },
  { when: (r) => !r.isActive && !r.isBlocked, style: { backgroundColor: "rgba(107,122,141,0.05)", opacity: 0.75 } },
];

/**
 * Main Customers page component.
 * @component
 * @returns {React.ReactElement}
 */
export default function Customers() {
  const navigate = useNavigate();

  // Data state
  const [customers, setCustomers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [limit, setLimit]           = useState(10);
  const [sortField, setSortField]   = useState("createdAt");
  const [sortDir, setSortDir]       = useState("desc");

  // Filter inputs (local — only applied on Search click or Enter)
  const [searchInput, setSearchInput]   = useState("");
  const [ssnInput, setSsnInput]         = useState("");
  const [stateFilter, setStateFilter]   = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Applied filters (what's actually sent to the API)
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedSsn, setAppliedSsn]       = useState("");

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState({ open: false, customerId: null, customerName: "" });

  /* ── Phone / search input formatting ── */
  const handleSearchInput = (val) => {
    // Auto-detect if user is typing a phone number (starts with digit or parenthesis)
    const looksLikePhone = /^[\d(]/.test(val.replace(/\s/g, ""));
    if (looksLikePhone && val.replace(/\D/g, "").length > 0) {
      const digits = val.replace(/\D/g, "").slice(0, 10);
      let formatted = digits;
      if (digits.length > 6)
        formatted = "(" + digits.slice(0,3) + ") " + digits.slice(3,6) + "-" + digits.slice(6);
      else if (digits.length > 3)
        formatted = "(" + digits.slice(0,3) + ") " + digits.slice(3);
      else if (digits.length > 0)
        formatted = "(" + digits;
      setSearchInput(formatted);
    } else {
      setSearchInput(val);
    }
  };

  const handleSsnInput = (val) => {
    const digits = val.replace(/\D/g, "").slice(0, 9);
    let formatted = digits;
    if (digits.length > 5)
      formatted = digits.slice(0,3) + "-" + digits.slice(3,5) + "-" + digits.slice(5);
    else if (digits.length > 3)
      formatted = digits.slice(0,3) + "-" + digits.slice(3);
    setSsnInput(formatted);
  };

  /* ── Apply search (button click or Enter) ── */
  const applySearch = () => {
    setAppliedSearch(searchInput);
    setAppliedSsn(ssnInput);
    setPage(1);
  };

  const clearFilters = () => {
    setSearchInput(""); setSsnInput("");
    setStateFilter(""); setStatusFilter("");
    setAppliedSearch(""); setAppliedSsn("");
    setPage(1);
  };

  /* ── Fetch ── */
  const lastFetchTimeRef   = useRef(0);
  const lastFetchParamsRef = useRef("");

  const fetchCustomers = useCallback(async () => {
    const params = {
      page,
      limit,
      sort: sortDir === "asc" ? sortField : `-${sortField}`,
    };
    if (appliedSearch) params.search = appliedSearch;
    if (appliedSsn)    params.socialNo = appliedSsn;
    if (stateFilter)   params.state = stateFilter;
    if (statusFilter)  params.isActive = statusFilter;

    // Deduplicate identical requests within 400ms (React StrictMode double-mount)
    const paramsKey = JSON.stringify(params);
    const now = Date.now();
    if (paramsKey === lastFetchParamsRef.current && now - lastFetchTimeRef.current < 400) {
      return;
    }
    lastFetchParamsRef.current = paramsKey;
    lastFetchTimeRef.current = now;

    setLoading(true);
    try {
      const res = await getCustomers(params);
      setCustomers(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("Failed to load customers: " + (err.response?.data?.message || err.message));
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, sortField, sortDir, appliedSearch, appliedSsn, stateFilter, statusFilter]);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  /* ── Sort / page handlers ── */
  const handleSort = (column, direction) => {
    setSortField(column.sortField || "createdAt");
    setSortDir(direction);
    setPage(1);
  };

  /* ── Actions ── */
  const handleBlockToggle = async (c) => {
    try {
      if (c.isBlocked) {
        const result = await Swal.fire({
          title: "Unblock Customer?",
          html: `<b>${formatName(c.firstName, c.lastName)}</b> will be allowed to make transactions again.`,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "✅ Yes, Unblock",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#16a34a",
          cancelButtonColor: "#6b7280",
        });
        if (!result.isConfirmed) return;
        await unblockCustomer(c._id);
        toast.success(`${c.firstName} ${c.lastName} has been unblocked.`);
      } else {
        const result = await Swal.fire({
          title: "🚫 Block Customer?",
          html: `
            <p style="margin-bottom:12px">You are about to block <b>${formatName(c.firstName, c.lastName)}</b>.<br/>They will not be able to make any transactions.</p>
            <input id="swal-block-reason" class="swal2-input" placeholder="Reason (e.g. Fraud, Suspicious activity)" style="width:80%" />
          `,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "🚫 Block Customer",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#dc2626",
          cancelButtonColor: "#6b7280",
          preConfirm: () => document.getElementById("swal-block-reason").value || "Fraud / Suspicious activity",
        });
        if (!result.isConfirmed) return;
        await blockCustomer(c._id, result.value);
        toast.success(`${c.firstName} ${c.lastName} has been blocked.`);
      }
      fetchCustomers();
    } catch {
      toast.error("Failed to update block status.");
    }
  };

  const handleDeactivate = (id, name) =>
    setConfirmModal({ open: true, customerId: id, customerName: name });

  const handleConfirmDeactivate = async () => {
    const { customerId, customerName } = confirmModal;
    setConfirmModal({ open: false, customerId: null, customerName: "" });
    try {
      await deleteCustomer(customerId);
      toast.success(`"${customerName}" has been deactivated.`);
      fetchCustomers();
    } catch {
      toast.error("Failed to deactivate customer.");
    }
  };

  /* ── Column definitions ── */
  const columns = [
    {
      name: "FIRST NAME",
      selector: (c) => c.firstName,
      sortable: true,
      sortField: "firstName",
      minWidth: "140px",
      cell: (c) => <AvatarCell customer={c} />,
    },
    {
      name: "LAST NAME",
      selector: (c) => c.lastName,
      sortable: true,
      sortField: "lastName",
      minWidth: "130px",
      cell: (c) => (
        <span style={{ fontWeight: 600, fontSize: 13.5 }}>
          {capitalizeName(c.lastName)}
        </span>
      ),
    },
    {
      name: "DATE OF BIRTH",
      selector: (c) => c.dateOfBirth,
      sortable: true,
      sortField: "dateOfBirth",
      minWidth: "130px",
      cell: (c) => {
        if (!c.dateOfBirth) return <span className="text-muted">—</span>;
        const formatted = formatDate(c.dateOfBirth);
        return <span className="td-date">{formatted}</span>;
      },
    },
    {
      name: "SSN",
      minWidth: "160px",
      cell: (c) => c.ssn
        ? <SsnCell ssn={c.ssn} />
        : <span className="text-muted">—</span>,
    },
    {
      name: "CONTACT",
      minWidth: "160px",
      cell: (c) => c.contacts?.[0]
        ? <div>
            <div style={{ fontSize: 13 }}>{c.contacts[0].value}</div>
            <div className="text-muted" style={{ fontSize: 11 }}>{c.contacts[0].type}</div>
          </div>
        : <span className="text-muted">—</span>,
    },
    {
      name: "ID TYPE",
      minWidth: "140px",
      cell: (c) => {
        const id = c.identifications?.[0];
        if (!id) return <span className="text-muted">—</span>;
        const expired = id.expiryDate && new Date(id.expiryDate) < new Date();
        return (
          <div>
            <div style={{ fontSize: 12.5, fontWeight: 600 }}>{id.type}</div>
            <div className="text-muted" style={{ fontSize: 11.5, fontFamily: "monospace" }}>{id.idNumber}</div>
            {expired && <div style={{ fontSize: 11, color: "var(--danger)", fontWeight: 700 }}>⚠️ EXPIRED</div>}
          </div>
        );
      },
    },
    {
      name: "STATUS",
      sortable: true,
      sortField: "isActive",
      minWidth: "110px",
      cell: (c) => (
        <span className={`badge ${c.isBlocked ? "badge-red" : c.isActive ? "badge-green" : "badge-gray"}`}>
          {c.isBlocked ? "🚫 Blocked" : c.isActive ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      name: "YEARLY ACTIVITY",
      selector: (c) => c.yearlyActivityTotal,
      sortable: true,
      sortField: "yearlyActivityTotal",
      right: true,
      minWidth: "140px",
      cell: (c) => (
        <span className="ytd-val ytd-val-primary">
          ${(c.yearlyActivityTotal || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}
        </span>
      ),
    },
    {
      name: "ADDED",
      selector: (c) => c.createdAt,
      sortable: true,
      sortField: "createdAt",
      minWidth: "110px",
      cell: (c) => (
        <span className="td-date">
          {formatDate(c.createdAt)}
        </span>
      ),
    },
    {
      name: "ACTIONS",
      minWidth: "180px",
      right: false,
      cell: (c) => (
        <div className="cust-actions" onClick={(e) => e.stopPropagation()}>
          <button className="btn btn-sm btn-outline" onClick={() => navigate(`/customers/${c._id}`)}>View</button>
          <button className="btn btn-sm btn-primary" onClick={() => navigate(`/cheques/new?customerId=${c._id}`)}>💵 Cash</button>
          <button
            className="btn btn-sm btn-ghost"
            style={{ color: c.isBlocked ? "var(--success)" : "#dc2626", fontSize: 12 }}
            onClick={() => handleBlockToggle(c)}
          >
            {c.isBlocked ? "✅ Unblock" : "🚫 Block"}
          </button>
          <button
            className="btn btn-sm btn-ghost"
            style={{ color: "var(--danger)" }}
            onClick={() => handleDeactivate(c._id, formatName(c.firstName, c.lastName))}
          >
            ✕
          </button>
        </div>
      ),
      ignoreRowClick: true,
    },
  ];

  const hasApplied = appliedSearch || appliedSsn || stateFilter || statusFilter;

  return (
    <Layout title="Customers">
      <ConfirmModal
        isOpen={confirmModal.open}
        customerName={confirmModal.customerName}
        onConfirm={handleConfirmDeactivate}
        onCancel={() => setConfirmModal({ open: false, customerId: null, customerName: "" })}
      />

      {/* Page heading */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22 }}>
            Customer Directory
          </h2>
          <p className="text-muted text-sm mt-1">
            {loading ? "Loading..." : `${total.toLocaleString("en-US")} customers total`}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={fetchCustomers} className="btn btn-outline btn-sm">🔄 Refresh</button>
          <Link to="/customers/new" className="btn btn-primary">➕ Add Customer</Link>
        </div>
      </div>

      {/* Filter bar */}
      <div className="filter-bar cust-filter-bar">
        {/* Search input with phone auto-format */}
        <div className="cust-search-wrap">
          <span className="cust-search-icon">🔍</span>
          <input
            type="text"
            className="form-control cust-search-input"
            placeholder="Search name, ID, phone..."
            value={searchInput}
            onChange={(e) => handleSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
          />
          {searchInput && (
            <button className="cust-search-clear" onClick={() => setSearchInput("")}>✕</button>
          )}
        </div>

        {/* SSN input */}
        <input
          type="text"
          className="form-control cust-ssn-input"
          placeholder="SSN XXX-XX-XXXX"
          value={ssnInput}
          onChange={(e) => handleSsnInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && applySearch()}
          style={{ fontFamily: "monospace" }}
        />

        {/* State */}
        <select
          className="form-control form-select"
          value={stateFilter}
          onChange={(e) => { setStateFilter(e.target.value); setPage(1); }}
        >
          <option value="">All States</option>
          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        {/* Status */}
        <select
          className="form-control form-select"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>

        {/* Search button */}
        <button className="btn btn-primary" onClick={applySearch}>
          🔍 Search
        </button>

        {/* Clear — only shown when something is actually applied */}
        {hasApplied && (
          <button className="btn btn-ghost btn-sm" onClick={clearFilters}>✕ Clear</button>
        )}
      </div>

      {/* DataTable */}
      <div className="card">
        <DataTable
          columns={columns}
          data={customers}
          progressPending={loading}
          progressComponent={
            <div className="table-loading-cell" style={{ padding: "56px 0" }}>
              <div className="spinner spinner-dark spinner-centered" style={{ width: 28, height: 28 }} />
              <div className="text-muted" style={{ marginTop: 10, fontSize: 13 }}>Loading customers...</div>
            </div>
          }
          noDataComponent={
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <h3>{hasApplied ? "No customers match your filters" : "No customers yet"}</h3>
              <p>{hasApplied ? "Try clearing the filters above." : "Add your first customer to get started."}</p>
              {hasApplied
                ? <button onClick={clearFilters} className="btn btn-outline btn-sm" style={{ marginTop: 10 }}>Clear Filters</button>
                : <Link to="/customers/new" className="btn btn-primary btn-sm" style={{ marginTop: 10 }}>➕ Add Customer</Link>
              }
            </div>
          }
          pagination
          paginationServer
          paginationTotalRows={total}
          paginationDefaultPage={page}
          paginationPerPage={limit}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          onChangePage={(p) => setPage(p)}
          onChangeRowsPerPage={(newLimit, newPage) => { setLimit(newLimit); setPage(newPage); }}
          sortServer
          onSort={handleSort}
          onRowClicked={(row) => navigate(`/customers/${row._id}`)}
          highlightOnHover
          pointerOnHover
          customStyles={dtStyles}
          conditionalRowStyles={conditionalRowStyles}
        />
      </div>
    </Layout>
  );
}
